import { HttpService } from "./HttpService";
import axios from "axios";
import store from "../store/store";
import moment from "moment";
import * as authDuck from "../store/ducks/auth.duck";

import { setLoggoutByRefreshToken } from "../utils/LocalStorage.js";

export class AppAPIService extends HttpService {
  static _instance;

  refreshPromise = null;

  isLoggingOut = false;

  constructor() {
    const instance = axios.create({
      baseURL: `${process.env.REACT_APP_API_HOST}/api`,
    });

    super(instance);
  }

  static getInstance() {
    return this._instance || (this._instance = new this());
  }

  async makeHttpRequest(config) {
    const authData = this.getAuthData();
    if (authData) {
      config.headers = {
        ...(config.headers || {}),
        Authorization: `Bearer ${authData.access_token}`,
        "Access-Control-Expose-Headers": "Content-Disposition",
      };
    }

    return super.makeHttpRequest(config);
  }

  async uploadRequest(formData, documentId, docUser) {
    const authData = this.getAuthData();
    if (authData) {
      try {
        const result = await axios.post(
          `${process.env.REACT_APP_API_HOST}/api/document/upload?documentId=${documentId}&typeDoc=${docUser}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${authData.access_token}`,
              "Access-Control-Expose-Headers": "Content-Disposition",
            },
          }
        );

        return result;
      } catch (error) {
        console.error(
          "🚀 ~ file: AppAPIService.js:54 ~ AppAPIService ~ uploadRequest ~ error:",
          error
        );
        throw error;
      }
    }

    throw new Error("Sessão inválida para envio de documentos.");
  }

  getAuthData() {
    const {
      auth: { authData },
    } = store.getState();

    return authData;
  }

  expireSession() {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;
    store.dispatch(authDuck.actions.logout());
    setLoggoutByRefreshToken();
    window.location.href = "/logout";
  }

  async handleRequestError(e, config) {
    if (e.response?.status !== 401) {
      return super.handleRequestError(e, config);
    }

    const {
      auth: { user, authData },
    } = store.getState();

    if (config.isLogin || config.isRefresh) {
      throw e;
    }

    if (config._retry || !authData?.refresh_token) {
      this.expireSession();
      throw e;
    }

    const requestAccessToken = config.headers?.Authorization;
    const currentAccessToken = `Bearer ${authData.access_token}`;

    // Outra requisicao pode ter renovado a sessao enquanto esta recebia 401.
    if (requestAccessToken && requestAccessToken !== currentAccessToken) {
      return this.makeHttpRequest({ ...config, _retry: true });
    }

    try {
      if (!this.refreshPromise) {
        const refreshConfig = {
          url: "/auth/refresh",
          method: "POST",
          isRefresh: true,
          sensitive: true,
          data: {
            refreshToken: authData.refresh_token,
            user,
          },
        };

        this.refreshPromise = super
          .makeHttpRequest(refreshConfig)
          .then(newAuthData => {
            store.dispatch(authDuck.actions.refresh(newAuthData));
            return newAuthData;
          })
          .finally(() => {
            this.refreshPromise = null;
          });
      }

      await this.refreshPromise;
      return this.makeHttpRequest({ ...config, _retry: true });
    } catch (error) {
      this.expireSession();
      throw error;
    }
  }

  async download(reqConfig, type) {
    const response = await this.makeHttpRequest({
      ...reqConfig,
      responseType: "blob",
    });

    const blob = new Blob([response.data], { type });

    const extensions = {
      "application/pdf": "pdf",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        "xlsx",
    };

    const contentDisposition = response.headers["content-disposition"];
    let fileName = `${moment().format("YYYY-MM-DD-HH-mm-ss")}.${
      extensions[type]
    }`;
    if (contentDisposition && contentDisposition.split("filename=").length) {
      fileName = contentDisposition.split("filename=")[1];
      if (fileName) {
        fileName = fileName.trim().substr(0, fileName.indexOf(";"));
      }
    }

    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      // IE variant
      window.navigator.msSaveOrOpenBlob(blob, fileName);
    } else {
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
    }
  }
}
